import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  updateProfile, 
  verifyGhanaCard, 
  updateDesignerSocials, 
  registerPushSubscription,
  fetchDesigner
} from '../api/client';
import Button from '../components/Button';
import Badge from '../components/Badge';
import { 
  Globe, 
  ShieldCheck, 
  Bell, 
  CheckCircle2, 
  Sparkles, 
  UserCheck,
  AlertCircle
} from 'lucide-react';
import { 
  BehanceIcon, 
  DribbbleIcon, 
  InstagramIcon, 
  TwitterXIcon, 
  LinkedInIcon 
} from '../components/SocialIcons';

export default function Settings() {
  const { user, dbUser } = useAuth();
  
  // Personal Info State
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [userType, setUserType] = useState('freelancer');
  const [interests, setInterests] = useState([]);
  const [specialization, setSpecialization] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  
  // Social Links State
  const [socials, setSocials] = useState({
    behance: '',
    dribbble: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    website: '',
  });

  const [file, setFile] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [pushStatus, setPushStatus] = useState('');

  useEffect(() => {
    if (dbUser) {
      setFirstName(dbUser.first_name || '');
      setLastName(dbUser.last_name || '');
      setPhoneNumber(dbUser.phone_number || '');
      setNationality(dbUser.nationality || '');
      setUserType(dbUser.user_type || 'freelancer');
      
      try {
        setInterests(dbUser.interests ? JSON.parse(dbUser.interests) : []);
      } catch (e) {
        setInterests([]);
      }
      
      // Fetch designer profile socials
      if (dbUser.display_name) {
        fetchDesigner(dbUser.display_name)
          .then(data => {
            if (data?.designer) {
              setBio(data.designer.bio || '');
              setLocation(data.designer.location || '');
              setSpecialization(data.designer.specialization || '');
              if (data.designer.social_links) {
                setSocials(prev => ({ ...prev, ...data.designer.social_links }));
              }
            }
          })
          .catch(() => {});
      }
    }
  }, [dbUser]);

  const toggleInterest = (interest) => {
    setInterests(prev => 
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    try {
      await updateProfile({
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber,
        nationality,
        user_type: userType,
        interests,
        specialization
      });

      // Save social links & bio to designer profile
      await updateDesignerSocials({
        bio,
        location,
        specialization,
        website_url: socials.website,
        social_links: socials,
      });

      setMessage('Profile and social portfolio updated successfully!');
    } catch (err) {
      setMessage(err.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!file) return;
    
    setVerifyLoading(true);
    setVerifyMessage('');

    try {
      await verifyGhanaCard(file);
      setVerifyMessage('Ghana Card Verified Successfully! 🎉 Reload the page to see your badge.');
    } catch (err) {
      setVerifyMessage(`Error: ${err.message}`);
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleEnableWebPush = async () => {
    if (!('Notification' in window)) {
      setPushStatus('Web push notifications are not supported in this browser.');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        // Register mock/live push token
        await registerPushSubscription({
          endpoint: `https://push.buildive.com/sub/${user?.id || 'guest'}_${Date.now()}`,
          keys: {
            p256dh: 'BNcRdreALRF8FsE8hST',
            auth: 'tBHItDaQqRW'
          }
        });
        setPushStatus('Web Push Notifications successfully enabled! You will be alerted on live DM chats & competition updates.');
      } else {
        setPushStatus('Notification permission was denied.');
      }
    } catch (err) {
      setPushStatus(`Failed to register push: ${err.message}`);
    }
  };

  if (!user) {
    return (
      <div className="page-container" style={{ padding: '80px 0', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-secondary)' }}>Please log in to view settings.</p>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ paddingBottom: '96px' }}>
      <div style={{ margin: '32px 0 28px' }}>
        <h1 className="text-h1">Account & Creator Settings</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          Manage your designer identity, public links, portfolio socials, and notification channels.
        </p>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '32px' }}>
        
        {/* ── Left Column: Personal & Socials Form ── */}
        <div className="glass-panel" style={{ padding: '36px', borderRadius: '20px', background: 'var(--bg-surface)' }}>
          <h3 className="text-h3" style={{ marginBottom: '20px' }}>Designer Profile & Socials</h3>
          
          {message && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '12px 16px', 
              backgroundColor: 'rgba(59, 246, 134, 0.12)', 
              border: '1px solid rgba(59, 246, 134, 0.3)',
              borderRadius: '10px', 
              color: 'var(--accent)',
              fontSize: '13.5px',
              fontWeight: 600
            }}>
              {message}
            </div>
          )}
          
          <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>First Name</label>
                <input type="text" className="form-input" value={firstName} onChange={e => setFirstName(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" className="form-input" value={lastName} onChange={e => setLastName(e.target.value)} />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Specialization / Title</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. Visual Brand & Motion Designer" 
                  value={specialization} 
                  onChange={e => setSpecialization(e.target.value)} 
                />
              </div>
              <div className="form-group">
                <label>Location / Country</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. Accra, Ghana" 
                  value={location} 
                  onChange={e => setLocation(e.target.value)} 
                />
              </div>
            </div>

            <div className="form-group">
              <label>Creator Biography</label>
              <textarea 
                rows={3} 
                className="form-input" 
                placeholder="Tell potential clients and collaborators about your design journey, tools, and background..."
                value={bio} 
                onChange={e => setBio(e.target.value)} 
              />
            </div>

            {/* ── Social Media & Contact Links ── */}
            <div style={{ marginTop: '8px', paddingTop: '18px', borderTop: '1px solid var(--border-subtle)' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '14px' }}>
                Portfolio & Social Showcase Links
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <BehanceIcon size={14} color="var(--primary)" /> Behance Profile
                  </label>
                  <input 
                    type="url" 
                    className="form-input" 
                    placeholder="https://behance.net/username" 
                    value={socials.behance} 
                    onChange={e => setSocials({ ...socials, behance: e.target.value })} 
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <DribbbleIcon size={14} color="#ea4c89" /> Dribbble Profile
                  </label>
                  <input 
                    type="url" 
                    className="form-input" 
                    placeholder="https://dribbble.com/username" 
                    value={socials.dribbble} 
                    onChange={e => setSocials({ ...socials, dribbble: e.target.value })} 
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <InstagramIcon size={14} color="#e1306c" /> Instagram Handle / URL
                  </label>
                  <input 
                    type="url" 
                    className="form-input" 
                    placeholder="https://instagram.com/username" 
                    value={socials.instagram} 
                    onChange={e => setSocials({ ...socials, instagram: e.target.value })} 
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <TwitterXIcon size={13} color="#1da1f2" /> Twitter / X Profile
                  </label>
                  <input 
                    type="url" 
                    className="form-input" 
                    placeholder="https://x.com/username" 
                    value={socials.twitter} 
                    onChange={e => setSocials({ ...socials, twitter: e.target.value })} 
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <LinkedInIcon size={14} color="#0077b5" /> LinkedIn Profile
                  </label>
                  <input 
                    type="url" 
                    className="form-input" 
                    placeholder="https://linkedin.com/in/username" 
                    value={socials.linkedin} 
                    onChange={e => setSocials({ ...socials, linkedin: e.target.value })} 
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Globe size={14} /> Personal Website / Portfolio
                  </label>
                  <input 
                    type="url" 
                    className="form-input" 
                    placeholder="https://yourportfolio.com" 
                    value={socials.website} 
                    onChange={e => setSocials({ ...socials, website: e.target.value })} 
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving Changes...' : 'Save Profile & Links'}
              </Button>
            </div>
          </form>
        </div>

        {/* ── Right Column: Verification & Web Push ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Ghana Card Verification */}
          <div className="glass-panel" style={{ padding: '28px', borderRadius: '20px', background: 'var(--bg-surface)' }}>
            <h3 className="text-h3" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldCheck size={20} className="text-green" /> Identity Verification
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '13.5px', lineHeight: 1.5 }}>
              Upload your Ghana Card to earn the verified creator checkmark on your public profile & template listings.
            </p>
            
            {dbUser?.ghana_card_verified ? (
              <div style={{ 
                padding: '16px', 
                backgroundColor: 'rgba(59, 246, 134, 0.1)', 
                border: '1px solid rgba(59, 246, 134, 0.3)',
                color: 'var(--accent)', 
                borderRadius: '12px', 
                fontWeight: 700, 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px' 
              }}>
                <CheckCircle2 size={18} /> Verified Ghanaian Pro Creator
              </div>
            ) : (
              <form onSubmit={handleVerify}>
                {verifyMessage && (
                  <div style={{ marginBottom: '14px', padding: '12px', fontSize: '13px', backgroundColor: 'var(--bg-hover)', borderRadius: '10px' }}>
                    {verifyMessage}
                  </div>
                )}
                
                <div className="form-group">
                  <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])} className="form-input" style={{ padding: '10px' }} />
                </div>
                
                <Button type="submit" variant="secondary" style={{ width: '100%', marginTop: '14px' }} disabled={!file || verifyLoading}>
                  {verifyLoading ? 'Verifying with AI...' : 'Submit Ghana Card'}
                </Button>
              </form>
            )}
          </div>

          {/* VAPID Web Push Notifications */}
          <div className="glass-panel" style={{ padding: '28px', borderRadius: '20px', background: 'var(--bg-surface)' }}>
            <h3 className="text-h3" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bell size={20} className="text-violet" /> Web Push Notifications
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '18px', fontSize: '13.5px', lineHeight: 1.5 }}>
              Get instant alerts for new direct messages, competition qualifier results, and marketplace template sales.
            </p>

            {pushStatus && (
              <div style={{ marginBottom: '14px', padding: '10px 14px', fontSize: '12.5px', background: 'var(--bg-hover)', borderRadius: '8px' }}>
                {pushStatus}
              </div>
            )}

            <Button variant="secondary" onClick={handleEnableWebPush} style={{ width: '100%' }}>
              <Bell size={16} /> Enable Browser Push Alerts
            </Button>
          </div>

        </div>

      </div>
    </div>
  );
}
